import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import { api } from '../utils/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { RootState } from '../store';

interface DashboardStats {
  total_users: number;
  active_users: {
    '7d': number;
    '14d': number;
    '30d': number;
  };
  total_meetings: number;
  total_time_recorded_minutes: number;
}

interface UserData {
  email: string;
  user_type: string;
  registered_at: string;
  total_meetings: number;
  avg_meetings_per_day: number;
  total_time_recorded_minutes: number;
}

interface TrendData {
  date: string;
  new_users: number;
  meetings: number;
}

interface SortConfig {
  key: keyof UserData;
  direction: 'asc' | 'desc';
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { token, user } = useSelector((state: RootState) => state.auth);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [users, setUsers] = useState<UserData[]>([]);
  const [trends, setTrends] = useState<TrendData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'email',
    direction: 'asc'
  });

  useEffect(() => {
    // Check if user is admin
    if (!user || user.user_type !== 'admin') {
      console.log('User is not admin:', user);
      setError('You do not have permission to access this page');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        console.log('Fetching admin dashboard data...');
        console.log('Auth token:', token);
        console.log('User:', user);

        // Make requests one by one to better track errors
        try {
          console.log('Fetching stats...');
          const statsRes = await api.get('/api/admin/dashboard/stats');
          console.log('Stats response:', statsRes.data);
          setStats(statsRes.data);
        } catch (error: any) {
          console.error('Error fetching stats:', error);
          throw error;
        }

        try {
          console.log('Fetching users...');
          const usersRes = await api.get('/api/admin/dashboard/users');
          console.log('Users response:', usersRes.data);
          setUsers(usersRes.data);
        } catch (error: any) {
          console.error('Error fetching users:', error);
          throw error;
        }

        try {
          console.log('Fetching trends...');
          const trendsRes = await api.get('/api/admin/dashboard/trends');
          console.log('Trends response:', trendsRes.data);
          setTrends(trendsRes.data);
        } catch (error: any) {
          console.error('Error fetching trends:', error);
          throw error;
        }

        setLoading(false);
      } catch (error: any) {
        console.error('Error fetching dashboard data:', error);
        console.error('Error response:', error.response);
        console.error('Error status:', error.response?.status);
        console.error('Error data:', error.response?.data);
        console.error('Error message:', error.message);
        console.error('Error config:', error.config);

        if (error.response?.status === 403) {
          setError('You do not have permission to access this page');
        } else if (error.response?.status === 401) {
          setError('Your session has expired. Please log in again.');
          // Redirect to login after a short delay
          setTimeout(() => navigate('/login'), 2000);
        } else if (!error.response) {
          setError('Network error: Could not connect to the server');
        } else {
          setError(error.response?.data?.detail || 'Failed to load dashboard data');
        }
        setLoading(false);
      }
    };

    fetchData();
  }, [token, user, navigate]);

  const handleSort = (key: keyof UserData) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedUsers = useMemo(() => {
    const sorted = [...users];
    sorted.sort((a, b) => {
      if (sortConfig.key === 'registered_at') {
        const dateA = new Date(a[sortConfig.key]).getTime();
        const dateB = new Date(b[sortConfig.key]).getTime();
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      if (typeof a[sortConfig.key] === 'number') {
        return sortConfig.direction === 'asc' 
          ? (a[sortConfig.key] as number) - (b[sortConfig.key] as number)
          : (b[sortConfig.key] as number) - (a[sortConfig.key] as number);
      }
      
      const valueA = String(a[sortConfig.key]).toLowerCase();
      const valueB = String(b[sortConfig.key]).toLowerCase();
      return sortConfig.direction === 'asc'
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    });
    return sorted;
  }, [users, sortConfig]);

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (loading || !stats) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading dashboard data...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Users
              </Typography>
              <Typography variant="h4">
                {stats.total_users}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Users (30d)
              </Typography>
              <Typography variant="h4">
                {stats.active_users['30d']}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Meetings
              </Typography>
              <Typography variant="h4">
                {stats.total_meetings}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Time Recorded
              </Typography>
              <Typography variant="h4">
                {Math.round(stats.total_time_recorded_minutes / 60)}h
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Trends Chart */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          User and Meeting Trends
        </Typography>
        <Box sx={{ height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="new_users" stroke="#8884d8" name="New Users" />
              <Line type="monotone" dataKey="meetings" stroke="#82ca9d" name="Meetings" />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* Users Table */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          User Details
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell 
                  onClick={() => handleSort('email')}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                >
                  Email {sortConfig.key === 'email' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell 
                  onClick={() => handleSort('user_type')}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                >
                  User Type {sortConfig.key === 'user_type' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell 
                  onClick={() => handleSort('registered_at')}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                >
                  Registered {sortConfig.key === 'registered_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell 
                  align="right"
                  onClick={() => handleSort('total_meetings')}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                >
                  Total Meetings {sortConfig.key === 'total_meetings' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell 
                  align="right"
                  onClick={() => handleSort('avg_meetings_per_day')}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                >
                  Avg Meetings/Day {sortConfig.key === 'avg_meetings_per_day' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell 
                  align="right"
                  onClick={() => handleSort('total_time_recorded_minutes')}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                >
                  Time Recorded (min) {sortConfig.key === 'total_time_recorded_minutes' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map((user) => (
                <TableRow key={user.email}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.user_type}</TableCell>
                  <TableCell>{new Date(user.registered_at).toLocaleDateString()}</TableCell>
                  <TableCell align="right">{user.total_meetings}</TableCell>
                  <TableCell align="right">{user.avg_meetings_per_day}</TableCell>
                  <TableCell align="right">{Math.round(user.total_time_recorded_minutes)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default AdminDashboard; 